/* ============================================================================
   bulk-mosaic.js — the auto-rotating customer photo wall
   ----------------------------------------------------------------------------
   WHAT IT IS
   An uneven, puzzle-like grid of customer photos. Every few seconds ONE tile
   flips over to reveal a different photo. Tiles are picked in a shuffled order
   and flip one at a time, so the wall feels alive without ever being busy
   enough to compete with the page's actual job — getting an enquiry.

   WHY ONE AT A TIME
   Flipping several tiles together reads as a slideshow and pulls the eye away
   from the form. A single quiet flip reads as "there are more photos than fit
   here", which is the impression we actually want.

   HOW MANY PHOTOS TO UPLOAD
   More than there are tiles. If there are 10 tiles and 10 photos, tiles just
   flip to photos already on screen and it looks broken. Aim for at least twice
   as many photos as tiles.

   PERFORMANCE
   • Tile count is fixed by the theme editor; extra photos cost one <img> each
     in a pool, loaded lazily as they're first shown.
   • The timer stops completely when the section is off-screen, and on hover,
     and when the browser tab is hidden.
   • Only `transform` animates.
   • Auto-rotation is disabled entirely for visitors who ask for reduced motion.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (!root || root.__mosaicInit) return;
    root.__mosaicInit = true;

    var cells = Array.prototype.slice.call(root.querySelectorAll('[data-mosaic-cell]'));
    if (!cells.length) return;

    var pool = [];
    try { pool = JSON.parse(root.querySelector('[data-mosaic-pool]').textContent || '[]'); } catch (e) {}

    var every = Math.max(1200, parseInt(root.dataset.every || '3200', 10));
    var timer = null;
    var running = false;
    var next = cells.length;      // first photo in the pool not already on the wall

    /* Shuffled visiting order so the same tile doesn't keep flipping.
       Deterministic — no Math.random — so behaviour is reproducible. */
    var order = cells.map(function (_, i) { return i; });
    for (var i = order.length - 1; i > 0; i--) {
      var j = (i * 7 + 3) % (i + 1);
      var t = order[i]; order[i] = order[j]; order[j] = t;
    }
    var cursor = 0;

    function flipOne() {
      if (!pool.length || pool.length <= cells.length) return;   // nothing new to show
      var cell = cells[order[cursor % order.length]];
      cursor++;

      var flipped = cell.classList.contains('is-flipped');
      // paint the face that is currently hidden, then turn it to the front
      var hidden = cell.querySelector(flipped ? '[data-face="front"]' : '[data-face="back"]');
      var img = hidden && hidden.querySelector('img');
      if (!img) return;

      var item = pool[next % pool.length];
      next++;
      img.src = item.src;
      img.alt = item.alt || '';
      var cap = hidden.querySelector('[data-face-caption]');
      if (cap) { cap.textContent = item.caption || ''; cap.style.display = item.caption ? '' : 'none'; }

      cell.classList.toggle('is-flipped');
    }

    function start() {
      if (running || reduceMotion) return;
      running = true;
      timer = setInterval(flipOne, every);
    }
    function stop() {
      running = false;
      if (timer) { clearInterval(timer); timer = null; }
    }

    root.addEventListener('pointerenter', stop);
    root.addEventListener('pointerleave', function () { if (visible) start(); });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else if (visible) start();
    });

    var visible = false;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          visible = en.isIntersecting;
          if (visible) start(); else stop();
        });
      }, { rootMargin: '100px' }).observe(root);
    } else {
      visible = true; start();
    }
  }

  function boot() { document.querySelectorAll('[data-bulk-mosaic]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
